import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, MessageSquare } from "lucide-react";
import { ClientReferralWithUI } from "@/api/clientReferrals";
import { ClientReferralNote } from "@/lib/schema";
import { formatDateForSouthAfrica } from "@/lib/textUtils";

// Client referral status type
type ClientReferralStatus = 'pending' | 'contacted' | 'meeting_scheduled' | 'converted' | 'rejected';

interface ClientNotesActionsTabProps {
  referral: ClientReferralWithUI;
  newNote: string;
  onNewNoteChange: (note: string) => void;
  onAddNote: () => void;
  onStatusChange: (status: ClientReferralStatus) => void;
}

export function ClientNotesActionsTab({ 
  referral, 
  newNote, 
  onNewNoteChange, 
  onAddNote, 
  onStatusChange, 
}: ClientNotesActionsTabProps) {
  // Get notes from referral - now properly typed
  const notes: ClientReferralNote[] = referral.notes || [];

  const getStatusLabel = (status: ClientReferralStatus) => {
    switch (status) {
      case 'pending': return 'PENDING';
      case 'contacted': return 'CONTACTED';
      case 'meeting_scheduled': return 'MEETING SCHEDULED';
      case 'converted': return 'CONVERTED';
      case 'rejected': return 'REJECTED';
    }
  };

  const getStatusButtonVariant = (status: ClientReferralStatus) => {
    switch (status) {
      case 'converted': return 'primary';
      case 'rejected': return 'destructive';
      default: return 'secondary';
    }
  };

  // Available status transitions - hide previous statuses, show current and future statuses
  const getAvailableStatuses = (currentStatus: ClientReferralStatus): ClientReferralStatus[] => {
    const statusOrder: ClientReferralStatus[] = ['pending', 'contacted', 'meeting_scheduled', 'converted', 'rejected'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    
    if (currentIndex === -1) return [];
    
    // For final statuses (converted, rejected), show no options
    if (currentStatus === 'converted' || currentStatus === 'rejected') {
      return [];
    }
    
    // Show all statuses from current position onwards, excluding current status
    const futureStatuses: ClientReferralStatus[] = statusOrder.slice(currentIndex + 1);
    
    // Always include 'rejected' as it can be reached from any non-final status
    if (!futureStatuses.includes('rejected')) {
      futureStatuses.push('rejected');
    }
    
    return futureStatuses;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">Status Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {getAvailableStatuses(referral.status as ClientReferralStatus).length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500 space-grotesk">
                  No status changes available
                </p>
                <p className="text-xs text-gray-400 space-grotesk mt-1">
                  Current status: {getStatusLabel(referral.status as ClientReferralStatus)}
                </p>
              </div>
            ) : (
              getAvailableStatuses(referral.status as ClientReferralStatus).map((status) => (
                <Button
                  key={status}
                  variant={getStatusButtonVariant(status)}
                  className="w-full justify-start space-grotesk hover:bg-gray-50"
                  onClick={() => onStatusChange(status)}
                  data-testid={`button-status-${status}`}
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Move to {getStatusLabel(status)}
                </Button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">Add Note</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={newNote}
              onChange={(e) => onNewNoteChange(e.target.value)}
              placeholder="Add your notes about this client referral..."
              className="space-grotesk"
              rows={4}
              data-testid="input-new-note"
            />
            <Button
              onClick={onAddNote}
              variant="primary"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white space-grotesk"
              data-testid="button-add-note"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Add Note
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="space-grotesk text-lg">Notes History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {notes.length === 0 ? (
              <p className="text-sm text-gray-500 space-grotesk text-center py-4">No notes added yet</p>
            ) : (
              notes
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by date, newest first
                .map((noteObj, index) => (
                  <div key={index} className="border-l-4 border-l-orange-500 pl-4 py-2">
                    <p className="text-sm text-gray-700 space-grotesk">{noteObj.note}</p>
                    <p className="text-xs text-gray-500 space-grotesk mt-1">
                      Added on {formatDateForSouthAfrica(noteObj.date)}
                    </p>
                  </div>
                ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
